import {
  all,
  call,
  put,
  SagaReturnType,
  select,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';
import { RequiredAny } from 'ui-kit/lib/types';

import { relationsActions as actions, relationsActions } from './slice';

import { errorHandler } from '../errorHandler';
import { notifyActions } from '../notifications/slice';
import { transformData } from '../results/helpers';
import { SagaPayload, SagaReturn } from '../types';

import {
  DynamicItem,
  ManualResultDetail,
  Result,
  ResultRequestType,
  ResultType,
} from '@/api/__generated__/webApi';
import { insurancesApi } from '@/api/insurances';
import { relationsApi } from '@/api/relations';
import i18n from '@/i18n/i18n';
import { deleteRelation } from '@/pages/Connections/fragments/Relations/helpers/functionHelpers';
import { envs } from '@/shared/constants/envs';
import { accessesActions } from '@/store/accesses/slice';

export function* relationsSaga() {
  yield all([
    takeLeading(actions.requestRelation, requestRelation),
    takeLeading(actions.requestDeleteRelation, requestDeleteRelation),
    takeLeading(actions.requestResultsById, requestResultsById),
    takeLeading(actions.requestResultDetails, requestResultDetails),
    takeLeading(actions.requestDynamics, requestDynamics),
    takeLeading(actions.editRelationAddressData, editRelationAddressData),
    takeLeading(actions.editRelationContactData, editRelationContactData),
    takeLeading(
      actions.editRelationHealthPoliciesData,
      editRelationHealthPoliciesData
    ),
    takeLeading(
      actions.requestPrimaryInsuranceCarriers,
      requestPrimaryInsuranceCarriers
    ),
    takeLeading(
      actions.requestSecondaryInsuranceCarriers,
      requestSecondaryInsuranceCarriers
    ),
    takeLeading(
      actions.requestPrimaryInsurancePlans,
      requestPrimaryInsurancePlans
    ),
    takeLeading(
      actions.requestSecondaryInsurancePlans,
      requestSecondaryInsurancePlans
    ),
    takeLeading(actions.requestRelations, requestRelations),
    takeLeading(actions.requestRelationRequests, requestRelationRequests),
    takeLeading(actions.approveRelationRequest, approveRelationRequest),
    takeLeading(actions.declineRelationRequest, declineRelationRequest),
    takeEvery(actions.deleteRelationRequest, deleteRelationRequest),
    takeLeading(actions.searchRelationContacts, searchRelationContacts),
    takeLeading(actions.sendRelationRequest, sendRelationRequest),
    takeLeading(actions.createRelationContact, createRelationContact),
    takeLeading(actions.deleteInsurance, deleteInsurance),
    takeLeading(actions.setDynamics, transformDynamicData),
    takeLeading(actions.sendResultsByEmail, sendResultsByEmail),
  ]);
}

function* transformDynamicData(): SagaReturn {
  try {
    yield put(actions.setFetching({ relation: true }));
    const dynamics: DynamicItem[] | null = yield select(
      (s) => s.relations.dynamics
    );
    console.info('warn, transformData executed');
    const { testResults }: Awaited<ReturnType<typeof transformData>> = dynamics
      ? yield call(transformData, dynamics)
      : { testResults: [] };
    yield put(actions.setTranformedData(testResults));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

export function* requestRelations(): SagaReturnType<RequiredAny> {
  try {
    yield put(actions.setFetching({ relation: true }));
    const relations: Awaited<ReturnType<typeof relationsApi.getRelations>> =
      yield call(relationsApi.getRelations);
    yield put(actions.setRelations({ relations }));
  } catch (e: RequiredAny) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

export function* requestRelationRequests(): SagaReturnType<RequiredAny> {
  try {
    yield put(actions.setFetching({ relation: true }));
    const relationRequests: Awaited<
      ReturnType<typeof relationsApi.getRelationRequests>
    > = yield call(relationsApi.getRelationRequests);
    yield put(actions.setRelationRequests({ relationRequests }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

export function* requestRelation({
  payload,
}: SagaPayload<typeof actions.requestRelation>) {
  try {
    yield put(actions.setFetching({ relation: true }));
    const relation: Awaited<ReturnType<typeof relationsApi.getRelationById>> =
      yield call(relationsApi.getRelationById, {
        relationId: payload.relationId,
      });
    yield put(actions.setRelation({ relation }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

export function* requestDeleteRelation({
  payload,
}: SagaPayload<typeof actions.requestDeleteRelation>) {
  try {
    yield put(actions.setFetching({ relation: true }));
    const brokeRelationsStatus: Awaited<ReturnType<typeof deleteRelation>> =
      yield call(deleteRelation, {
        relations: payload.relations,
        types: payload.types,
        relationId: payload.relationId,
      });
    const relations: Awaited<ReturnType<typeof relationsApi.getRelations>> =
      yield call(relationsApi.getRelations);
    yield put(actions.setRelations({ relations }));
    const isStatusError = Object.values(brokeRelationsStatus).find(
      (status) => status !== 200
    );
    const step = isStatusError ? 'error' : 'success';
    yield put(actions.setBrokeRelationsStatus({ brokeRelationsStatus }));
    yield put(actions.setStep({ step }));
  } catch (e) {
    yield put(actions.setStep({ step: 'error' }));
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

export function* requestResultsById({
  payload,
}: SagaPayload<typeof actions.requestResultsById>) {
  try {
    yield put(actions.setFetching({ relation: true }));
    const results: Awaited<ReturnType<typeof relationsApi.getResultsById>> =
      yield call(relationsApi.getResultsById, {
        relationId: payload.relationId,
      });
    yield put(actions.setResults({ results }));
  } catch (e: RequiredAny) {
    if (e.status === 500 || !e.status) {
      yield put(
        notifyActions.showNotifications([
          {
            id: '',
            type: 'error',
            text: i18n.t('SMTH_WENT_WRONG', {
              link: envs.PATIENT_CONTACT_SUPPORT_URL,
            }),
          },
        ])
      );
    }
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

function* requestResultDetails({
  payload,
}: SagaPayload<typeof actions.requestResultDetails>): SagaReturn {
  try {
    yield put(actions.setFetching({ relation: true }));
    const details: Awaited<ReturnType<typeof relationsApi.getResultDetails>> =
      yield call(relationsApi.getResultDetails, payload);

    if (payload.type === ResultType.MANUAL) {
      yield put(actions.setManualDetails(details as ManualResultDetail));
    } else {
      yield put(actions.setLabDetails(details as Result));
    }
  } catch (e: RequiredAny) {
    yield put(actions.setErrorStatus(e.status));
    if (e.status === 500 || !e.status) {
      yield put(
        notifyActions.showNotifications([
          {
            id: '',
            type: 'error',
            text: i18n.t('SMTH_WENT_WRONG', {
              link: envs.PATIENT_CONTACT_SUPPORT_URL,
            }),
          },
        ])
      );
    }
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

function* requestDynamics({
  payload,
}: SagaPayload<typeof actions.requestResultsById>) {
  try {
    yield put(actions.setFetching({ dynamics: true }));
    const dynamics: Awaited<
      ReturnType<typeof relationsApi.searchRelationDynamicsById>
    > = yield call(relationsApi.searchRelationDynamicsById, {
      relationId: payload.relationId,
      data: { type: ResultRequestType.ALL },
    });
    yield put(actions.setDynamics(dynamics));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ dynamics: false }));
  }
}

function* editRelationAddressData({
  payload,
}: SagaPayload<typeof actions.editRelationAddressData>): SagaReturn {
  try {
    yield put(actions.setSave(true));
    yield call(relationsApi.editRelationAddressData, {
      relationId: payload.relationId,
      data: payload.data,
    });
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setSave(false));
  }
}

function* editRelationContactData({
  payload,
}: SagaPayload<typeof actions.editRelationContactData>): SagaReturn {
  try {
    yield put(actions.setSave(true));
    yield call(relationsApi.editRelationContactData, {
      relationId: payload.relationId,
      data: payload.data,
    });
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setSave(false));
  }
}

function* editRelationHealthPoliciesData({
  payload,
}: SagaPayload<typeof actions.editRelationHealthPoliciesData>): SagaReturn {
  try {
    yield call(relationsApi.editRelationHealthPoliciesData, {
      relationId: payload.relationId,
      data: payload.data,
    });
    const relation: Awaited<ReturnType<typeof relationsApi.getRelationById>> =
      yield call(relationsApi.getRelationById, {
        relationId: payload.relationId,
      });
    yield put(actions.setRelation({ relation }));
    yield put(actions.setHasInsuranceError(false));
  } catch (e) {
    yield put(
      notifyActions.showNotifications([
        { id: '', type: 'error', text: i18n.t('THE_INSURANCE_NOT_BEEN_SAVED') },
      ])
    );
    yield put(actions.setHasInsuranceError(true));
    errorHandler(e, actions.setCommonError);
  }
}

function* requestPrimaryInsuranceCarriers({
  payload,
}: SagaPayload<typeof actions.requestPrimaryInsuranceCarriers>) {
  try {
    yield put(actions.setFetching({ relation: true }));
    const insuranceCarriers: Awaited<
      ReturnType<typeof insurancesApi.getInsuranceCarriersList>
    > = yield call(insurancesApi.getInsuranceCarriersList, {
      filter: payload.filter,
    });
    yield put(actions.setPrimaryInsuranceCarriers({ insuranceCarriers }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

function* requestSecondaryInsuranceCarriers({
  payload,
}: SagaPayload<typeof actions.requestSecondaryInsuranceCarriers>) {
  try {
    yield put(actions.setFetching({ relation: true }));
    const insuranceCarriers: Awaited<
      ReturnType<typeof insurancesApi.getInsuranceCarriersList>
    > = yield call(insurancesApi.getInsuranceCarriersList, {
      filter: payload.filter,
    });
    yield put(actions.setSecondaryInsuranceCarriers({ insuranceCarriers }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

function* requestPrimaryInsurancePlans({
  payload,
}: SagaPayload<typeof actions.requestPrimaryInsurancePlans>) {
  try {
    yield put(actions.setFetching({ relation: true }));
    const insurancePlans: Awaited<
      ReturnType<typeof insurancesApi.getInsurancePlansList>
    > = yield call(insurancesApi.getInsurancePlansList, {
      insuranceCarrierId: payload.insuranceCarrierId,
    });
    yield put(actions.setPrimaryInsurancePlans({ insurancePlans }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

function* requestSecondaryInsurancePlans({
  payload,
}: SagaPayload<typeof actions.requestSecondaryInsurancePlans>) {
  try {
    yield put(actions.setFetching({ relation: true }));
    const insurancePlans: Awaited<
      ReturnType<typeof insurancesApi.getInsurancePlansList>
    > = yield call(insurancesApi.getInsurancePlansList, {
      insuranceCarrierId: payload.insuranceCarrierId,
    });
    yield put(actions.setSecondaryInsurancePlans({ insurancePlans }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

function* approveRelationRequest({
  payload,
}: SagaPayload<typeof actions.approveRelationRequest>): SagaReturn {
  try {
    yield call(relationsApi.approveRelationRequest, payload);
    const relations: Awaited<ReturnType<typeof relationsApi.getRelations>> =
      yield call(relationsApi.getRelations);
    yield put(actions.setRelations({ relations }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  }
}

function* declineRelationRequest({
  payload,
}: SagaPayload<typeof actions.declineRelationRequest>): SagaReturn {
  try {
    yield call(relationsApi.declineRelationRequest, payload);
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  }
}

function* deleteRelationRequest({
  payload,
}: SagaPayload<typeof actions.deleteRelationRequest>): SagaReturn {
  try {
    yield call(relationsApi.deleteRelationRequest, payload);
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  }
}

function* searchRelationContacts({
  payload,
}: SagaPayload<typeof actions.searchRelationContacts>): SagaReturn {
  try {
    yield put(actions.setFetching({ relation: true }));
    const relationContacts: Awaited<
      ReturnType<typeof relationsApi.searchRelationContacts>
    > = yield call(relationsApi.searchRelationContacts, {
      data: payload.data,
    });
    yield put(
      actions.setRelationContacts({
        relationContacts: relationContacts.items || null,
      })
    );
    yield put(
      actions.setRelationContactsHasNext({
        relationContactsHasNext: relationContacts.hasNext || null,
      })
    );
  } catch (e) {
    errorHandler(e, actions.setCommonError);
    yield put(
      notifyActions.showNotifications([
        {
          id: '',
          type: 'error',
          text: i18n.t('SMTH_WENT_WRONG', {
            link: envs.PATIENT_CONTACT_SUPPORT_URL,
          }),
        },
      ])
    );
  } finally {
    yield put(actions.setFetching({ relation: false }));
  }
}

function* sendRelationRequest({
  payload,
}: SagaPayload<typeof actions.sendRelationRequest>): SagaReturn {
  try {
    const data: Awaited<ReturnType<typeof relationsApi.sendRelationRequest>> =
      yield call(relationsApi.sendRelationRequest, { data: payload.data });
    yield put(
      actions.setFailureCreatedContact({ failureCreatedContact: null })
    );
    yield put(relationsActions.setRelationRequestData(data));
    yield put(relationsActions.setStep({ step: 'open' }));
    yield put(relationsActions.requestRelationRequests());
    yield put(accessesActions.requestAccessesRequests());
  } catch (e) {
    yield put(
      notifyActions.showNotifications([
        {
          id: '',
          type: 'error',
          text: i18n.t('SMTH_WENT_WRONG', {
            link: envs.PATIENT_CONTACT_SUPPORT_URL,
          }),
        },
      ])
    );
    errorHandler(e, actions.setCommonError);
  }
}

function* createRelationContact({
  payload,
}: SagaPayload<typeof actions.createRelationContact>): SagaReturn {
  try {
    yield put(actions.setStep({ step: 'loading' }));
    const successCreatedContact: Awaited<
      ReturnType<typeof relationsApi.createRelationContact>
    > = yield call(relationsApi.createRelationContact, {
      data: payload.data,
      force: payload.force,
    });
    yield put(
      actions.setSuccessCreatedContact({ successCreatedContact } as never)
    );
    yield put(actions.setStep({ step: 'init' }));
    yield put(actions.setBirthOfDateError({ error: null }));
  } catch (e: RequiredAny) {
    if (e?.status === 409) {
      yield put(actions.setStep({ step: 'loading' }));
      const failureCreatedContact = e?.data;
      yield put(actions.setFailureCreatedContact({ failureCreatedContact }));
      yield put(actions.setStep({ step: 'init' }));
    } else {
      yield put(actions.setStep({ step: 'error' }));
      const birthDateError = e?.data?.error;
      const emailError = e?.data?.error;
      yield put(actions.setBirthOfDateError({ error: birthDateError }));
      yield put(
        actions.setEmailError({ error: emailError, email: payload.data.email })
      );
      errorHandler(e, actions.setCommonError);
    }
  }
}

function* deleteInsurance({
  payload,
}: SagaPayload<typeof actions.deleteInsurance>): SagaReturn {
  try {
    yield put(actions.setInsuranceStep({ step: 'loading' }));
    yield call(relationsApi.editRelationHealthPoliciesData, {
      data: payload.data,
      relationId: payload.relationId,
    });
    const relation: Awaited<ReturnType<typeof relationsApi.getRelationById>> =
      yield call(relationsApi.getRelationById, {
        relationId: payload.relationId,
      });
    yield put(actions.setRelation({ relation }));
    yield put(actions.setInsuranceStep({ step: 'init' }));
  } catch (e) {
    yield put(
      notifyActions.showNotifications([
        { id: '', type: 'error', text: i18n.t('INSURANCE_ERROR_DELETE') },
      ])
    );
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setInsuranceStep({ step: 'init' }));
  }
}

function* sendResultsByEmail({
  payload,
}: SagaPayload<typeof actions.sendResultsByEmail>): SagaReturn {
  try {
    yield call(relationsApi.sendResultsByEmail, {
      relationId: payload.relationId,
      resultId: payload.resultId,
      data: {
        email: payload.email,
        type: payload.type,
      },
    });
    yield put(actions.setSendResultStep({ step: 'success' }));
  } catch (e) {
    yield put(actions.setSendResultStep({ step: 'error' }));
    errorHandler(e, actions.setCommonError);
  }
}
